export type AppKind = 'facility' | 'osc';
export type EnvironmentName = 'development' | 'uat' | 'production';

export interface AppConfig {
  appKind: AppKind;
  appName: string;
  shortName: string;
  description: string;
  environment: EnvironmentName;
  apiBaseUrl: string;
  accent: string;
  modules: readonly ModuleLink[];
}

export interface ModuleLink {
  id: string;
  label: string;
  description: string;
  status: 'available' | 'planned';
}

export interface SessionUser {
  sub: string;
  email: string;
  name: string;
  picture?: string;
  hd: string;
  role?: string;
  facility_code?: string;
  facility_type?: string;
  auth_mode?: 'google' | 'mock';
}

export function normalizeApiBaseUrl(value: string | undefined): string {
  const url = (value || '/api').trim();
  return url.endsWith('/') ? url.slice(0, -1) : url;
}

export async function callServer<T>(
  config: Pick<AppConfig, 'apiBaseUrl'>,
  method: string,
  ...params: unknown[]
): Promise<T> {
  const response = await fetch(`${config.apiBaseUrl}/rpc`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'eDICE-PWA'
    },
    body: JSON.stringify({ method, params })
  });

  if (!response.ok) {
    throw new Error(`Permintaan gagal (${response.status})`);
  }

  return response.json() as Promise<T>;
}

function escapeHtml(value: string): string {
  return value.replace(
    /[&<>"']/g,
    (character) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' })[character] || character
  );
}

function getInitials(user: SessionUser): string {
  const parts = user.name.trim().split(/\s+/).filter(Boolean);
  return (parts.length > 1 ? `${parts[0][0]}${parts.at(-1)?.[0]}` : user.name.slice(0, 2)).toUpperCase();
}

export function mountAppShell(root: HTMLElement, config: AppConfig, user?: SessionUser): void {
  document.documentElement.style.setProperty('--app-accent', config.accent);
  document.title = `${config.appName} · ${config.environment.toUpperCase()}`;

  root.innerHTML = `
    <div class="app-shell">
      <aside class="sidebar" aria-label="Navigasi utama">
        <div class="brand">
          <span class="brand-mark" aria-hidden="true">${config.shortName.slice(0, 1)}</span>
          <span>
            <strong>${config.shortName}</strong>
            <small>${config.appKind === 'facility' ? 'Fasiliti' : 'Negeri'}</small>
          </span>
        </div>
        <nav>
          <a class="nav-link is-active" href="#utama">Utama</a>
          ${config.modules
            .map(
              (module) =>
                `<a class="nav-link" href="#${module.id}" aria-disabled="${module.status === 'planned'}">${module.label}</a>`
            )
            .join('')}
        </nav>
        <div class="environment">${config.environment.toUpperCase()} · Data dummy sahaja</div>
      </aside>

      <main>
        <header class="topbar">
          <div>
            <p class="eyebrow">BPF Negeri Perak</p>
            <h1>${config.appName}</h1>
          </div>
          <button class="profile-button" id="logout-button" type="button" ${user ? '' : 'disabled'}>
            <span aria-hidden="true">${user ? escapeHtml(getInitials(user)) : 'MOH'}</span>
            <span>${user ? escapeHtml(user.name) : 'Akaun MOH'}<small>${user ? escapeHtml(user.email) : 'OAuth belum dikonfigurasi'}</small></span>
          </button>
        </header>

        <section class="welcome-card" id="utama">
          <div>
            <span class="status-pill">PWA Foundation</span>
            <h2>Foundation aplikasi telah tersedia.</h2>
            <p>${config.description}</p>
          </div>
          <div class="readiness" aria-label="Status foundation">
            <strong>DEV</strong>
            <span id="backend-status">Menyemak sambungan backend…</span>
          </div>
        </section>

        <section aria-labelledby="module-heading">
          <div class="section-heading">
            <div>
              <p class="eyebrow">Modul</p>
              <h2 id="module-heading">Ruang kerja utama</h2>
            </div>
            <span>${config.modules.length} modul dirancang</span>
          </div>
          <div class="module-grid">
            ${config.modules
              .map(
                (module) => `
                  <article class="module-card" id="${module.id}">
                    <span class="module-icon" aria-hidden="true">${module.label.slice(0, 2).toUpperCase()}</span>
                    <div>
                      <h3>${module.label}</h3>
                      <p>${module.description}</p>
                    </div>
                    <span class="module-status ${module.status}">${module.status === 'available' ? 'Foundation' : 'Dirancang'}</span>
                  </article>`
              )
              .join('')}
          </div>
        </section>
      </main>
    </div>
  `;
}
